import { useCallback } from 'react'
import { useLanguage } from '@context'
import { usePaywall } from '@context'
import { getPlanLimitCode, getPlanLimitParams, isPlanLimitError, showApiErrorToast } from '@utils'

/**
 * Central mutation/query error handler.
 *
 * When the API refuses an action because a free-tier limit is reached (HTTP
 * 402 + a `PLAN_*` code), it raises the Premium paywall instead of a raw
 * error toast. Every other error keeps the existing toast behaviour.
 *
 * Use it in the `onError` of any mutation that can hit a plan limit
 * (contact/ghost-profile create, note create, reminder create).
 */
export const useApiErrorHandler = () => {
  const { t } = useLanguage()
  const { showPaywall } = usePaywall()

  return useCallback(
    (error: unknown) => {
      if (isPlanLimitError(error)) {
        showPaywall({ code: getPlanLimitCode(error), params: getPlanLimitParams(error) })
        return
      }
      showApiErrorToast(t, error)
    },
    [t, showPaywall],
  )
}
